import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import { getUploadUrl, getCloudinaryThumbnail } from "@/lib/utils";
import { Maximize2, Minimize2, X, ChevronLeft, ChevronRight, Locate } from "lucide-react";

const injectedStyle = `
.photo-cluster {
  display: flex; align-items: center; justify-content: center;
  border-radius: 10px; font-weight: 700; font-size: 13px;
  color: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.4);
  border: 3px solid rgba(255,255,255,0.3);
}
.photo-cluster-small { background: #f97316; width: 36px; height: 36px; }
.photo-cluster-medium { background: #ea580c; width: 44px; height: 44px; font-size: 14px; }
.photo-cluster-large { background: #dc2626; width: 52px; height: 52px; font-size: 15px; }
.photo-preview-overlay {
  position: fixed; inset: 0; z-index: 10002;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,0.85); backdrop-filter: blur(4px);
}
.photo-preview-img {
  max-width: 90%; max-height: 85%; border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.6); object-fit: contain;
}
.photo-preview-close {
  position: absolute; top: 12px; right: 12px;
  background: rgba(0,0,0,0.6); border: 1px solid rgba(255,255,255,0.2);
  color: #fff; border-radius: 8px; padding: 6px; cursor: pointer;
  transition: background 0.2s;
}
.photo-preview-close:hover { background: rgba(0,0,0,0.8); }
.photo-preview-nav {
  position: absolute; top: 50%; transform: translateY(-50%);
  background: rgba(0,0,0,0.6); border: 1px solid rgba(255,255,255,0.2);
  color: #fff; border-radius: 50%; width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.2s;
}
.photo-preview-nav:hover { background: rgba(0,0,0,0.8); }
.photo-preview-nav-prev { left: 16px; }
.photo-preview-nav-next { right: 16px; }
.photo-preview-caption {
  position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%);
  background: rgba(0,0,0,0.7); color: #fff; padding: 6px 16px;
  border-radius: 8px; font-size: 13px; max-width: 80%; text-align: center;
}
`;

// Shared function to build markers on a map
function buildMarkers(map, photos, clusterRef, onMarkerClick, boundsRef = null) {
  if (clusterRef.current) {
    map.removeLayer(clusterRef.current);
    clusterRef.current = null;
  }

  const gpsPhotos = photos.filter((p) => p.gps?.lat && p.gps?.lng);
  if (gpsPhotos.length === 0) return;

  const cluster = L.markerClusterGroup({
    maxClusterRadius: 45,
    spiderfyOnMaxZoom: true,
    showCoverageOnHover: false,
    zoomToBoundsOnClick: true,
    spiderfyDistanceMultiplier: 1.8,
    iconCreateFunction: (c) => {
      const count = c.getChildCount();
      let size = "small";
      if (count >= 20) size = "large";
      else if (count >= 5) size = "medium";
      const dim = size === "large" ? 52 : size === "medium" ? 44 : 36;
      return L.divIcon({
        html: `<div class="photo-cluster photo-cluster-${size}">${count}</div>`,
        className: "",
        iconSize: L.point(dim, dim),
      });
    },
  });

  const bounds = [];

  gpsPhotos.forEach((photo) => {
    const lat = photo.gps.lat;
    const lng = photo.gps.lng;
    bounds.push([lat, lng]);

    const photoUrl = getUploadUrl(photo.filename);
    const thumbUrl = getCloudinaryThumbnail(photoUrl, 80, 80);
    const fullUrl = getCloudinaryThumbnail(photoUrl, 1200, 900);

    const icon = L.divIcon({
      className: "photo-map-marker",
      html: `<div style="
        width: 44px; height: 44px; border-radius: 8px; overflow: hidden;
        border: 3px solid ${photo.segment_label ? "#f97316" : "#eab308"};
        box-shadow: 0 2px 8px rgba(0,0,0,0.4); cursor: pointer;
        background: #1a1a1a;
      ">
        <img src="${thumbUrl}" style="width: 100%; height: 100%; object-fit: cover;" loading="lazy" />
      </div>`,
      iconSize: [44, 44],
      iconAnchor: [22, 44],
    });

    const marker = L.marker([lat, lng], { icon });
    marker.on("click", () => {
      const parts = [photo.segment_label, photo.caption_text].filter(Boolean);
      const caption = parts.join(" — ");
      onMarkerClick({ url: photoUrl, fullUrl, caption, segmentLabel: photo.segment_label || null, filename: photo.filename });
    });

    cluster.addLayer(marker);
  });

  map.addLayer(cluster);
  clusterRef.current = cluster;
  if (boundsRef) boundsRef.current = bounds;
  map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
}

// Reusable photo preview overlay with navigation
function PhotoPreviewOverlay({ photos, currentFilename, onClose, onNavigate }) {
  const gpsPhotos = useMemo(() => photos.filter((p) => p.gps?.lat && p.gps?.lng), [photos]);
  const currentIdx = useMemo(() => gpsPhotos.findIndex((p) => p.filename === currentFilename), [gpsPhotos, currentFilename]);
  const photo = currentIdx >= 0 ? gpsPhotos[currentIdx] : null;

  const goPrev = useCallback((e) => {
    e.stopPropagation();
    if (currentIdx > 0) onNavigate(gpsPhotos[currentIdx - 1].filename);
  }, [currentIdx, gpsPhotos, onNavigate]);

  const goNext = useCallback((e) => {
    e.stopPropagation();
    if (currentIdx < gpsPhotos.length - 1) onNavigate(gpsPhotos[currentIdx + 1].filename);
  }, [currentIdx, gpsPhotos, onNavigate]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowLeft" && currentIdx > 0) onNavigate(gpsPhotos[currentIdx - 1].filename);
      else if (e.key === "ArrowRight" && currentIdx < gpsPhotos.length - 1) onNavigate(gpsPhotos[currentIdx + 1].filename);
      else if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [currentIdx, gpsPhotos, onNavigate, onClose]);

  if (!photo) return null;

  const fullUrl = getCloudinaryThumbnail(getUploadUrl(photo.filename), 1200, 900);
  const parts = [photo.segment_label, photo.caption_text].filter(Boolean);
  const caption = parts.join(" — ");

  return (
    <div className="photo-preview-overlay" onClick={onClose}>
      <button type="button" className="photo-preview-close" onClick={onClose}>
        <X size={18} />
      </button>
      {currentIdx > 0 && (
        <button type="button" className="photo-preview-nav photo-preview-nav-prev" onClick={goPrev} data-testid="photo-preview-prev">
          <ChevronLeft size={22} />
        </button>
      )}
      {currentIdx < gpsPhotos.length - 1 && (
        <button type="button" className="photo-preview-nav photo-preview-nav-next" onClick={goNext} data-testid="photo-preview-next">
          <ChevronRight size={22} />
        </button>
      )}
      <img src={fullUrl} alt={caption} className="photo-preview-img" style={{ cursor: "pointer" }} onClick={onClose} />
      {(caption || gpsPhotos.length > 1) && (
        <div className="photo-preview-caption">
          {caption}{gpsPhotos.length > 1 && ` (${currentIdx + 1}/${gpsPhotos.length})`}
        </div>
      )}
    </div>
  );
}

// Fullscreen map rendered via portal (escapes all ancestor backdrop-filter/transform)
function FullscreenMap({ photos, onClose, onPhotoClick }) {
  const { t } = useTranslation("common");
  const overlayRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const clusterRef = useRef(null);
  const boundsRef = useRef(null);
  const [previewFilename, setPreviewFilename] = useState(null);
  const [isNative, setIsNative] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Track native (browser) fullscreen state and re-fit the map on change.
  useEffect(() => {
    const onFsChange = () => {
      const active = !!document.fullscreenElement;
      setIsNative(active);
      setTimeout(() => mapInstance.current?.invalidateSize(), 120);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  useEffect(() => {
    if (previewFilename) return; // Don't handle Escape when preview is open (preview handles it)
    const handler = (e) => {
      // Esc in native fullscreen is handled by the browser; only close the
      // overlay when we're NOT in native fullscreen.
      if (e.key === "Escape" && !document.fullscreenElement) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose, previewFilename]);

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: true }).setView([39.5, -8.0], 6);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 18,
    }).addTo(map);
    mapInstance.current = map;

    buildMarkers(map, photos, clusterRef, (info) => {
      if (onPhotoClick) {
        onPhotoClick(info);
      } else {
        setPreviewFilename(info.filename);
      }
    }, boundsRef);

    return () => { map.remove(); mapInstance.current = null; };
  }, [photos, onPhotoClick]);

  const recenter = useCallback(() => {
    if (mapInstance.current && boundsRef.current?.length) {
      mapInstance.current.fitBounds(boundsRef.current, { padding: [30, 30], maxZoom: 14 });
    }
  }, []);

  // AUMENTAR: maximizado (overlay) → ecrã total nativo.
  const increaseLevel = useCallback(() => {
    if (!document.fullscreenElement) {
      overlayRef.current?.requestFullscreen?.().catch(() => {});
    }
  }, []);

  // DIMINUIR: ecrã total nativo → overlay → fechar (volta ao inline).
  const decreaseLevel = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      onClose();
    }
  }, [onClose]);

  return (
    <div ref={overlayRef} style={{ position: "fixed", inset: 0, zIndex: 10001, background: "#0a0a0a" }} data-testid="photos-map-fullscreen-overlay">
      <div className="absolute top-3 right-3 z-[10002] flex gap-1.5">
        <button
          type="button"
          onClick={recenter}
          className="bg-zinc-800/90 border border-zinc-600 rounded-md p-2 hover:bg-zinc-700 transition-colors text-white"
          data-testid="photos-map-fullscreen-recenter"
          aria-label={t("map.controls.recenter", "Centrar na totalidade da rota")}
          title={t("map.controls.recenter", "Centrar na totalidade da rota")}
        >
          <Locate className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={increaseLevel}
          disabled={isNative}
          className="bg-zinc-800/90 border border-zinc-600 rounded-md p-2 hover:bg-zinc-700 transition-colors text-white disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-zinc-800/90"
          data-testid="photos-map-fullscreen-increase"
          aria-label={t("map.controls.enter_fullscreen", "Ecrã completo")}
          title={t("map.controls.enter_fullscreen", "Ecrã completo")}
        >
          <Maximize2 className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={decreaseLevel}
          className="bg-zinc-800/90 border border-zinc-600 rounded-md p-2 hover:bg-zinc-700 transition-colors text-white"
          data-testid="photos-map-fullscreen-close"
          aria-label={isNative ? t("map.controls.close_fullscreen", "Fechar ecrã completo") : t("map.controls.reduce", "Reduzir mapa")}
          title={isNative ? t("map.controls.close_fullscreen", "Fechar ecrã completo") : t("map.controls.reduce", "Reduzir mapa")}
        >
          <Minimize2 className="h-5 w-5" />
        </button>
      </div>
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      {previewFilename && (
        <PhotoPreviewOverlay
          photos={photos}
          currentFilename={previewFilename}
          onClose={() => setPreviewFilename(null)}
          onNavigate={setPreviewFilename}
        />
      )}
    </div>
  );
}

export function PhotosMap({ photos, onPhotoClick, className }) {
  const { t } = useTranslation("common");
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const clusterRef = useRef(null);
  const boundsRef = useRef(null);
  const styleRef = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [previewFilename, setPreviewFilename] = useState(null);

  // Inject styles once
  useEffect(() => {
    if (!styleRef.current) {
      styleRef.current = document.createElement("style");
      styleRef.current.textContent = injectedStyle;
      document.head.appendChild(styleRef.current);
    }
    return () => { if (styleRef.current) { styleRef.current.remove(); styleRef.current = null; } };
  }, []);

  // Escape key handler (inline map only)
  useEffect(() => {
    if (fullscreen || previewFilename) return;
    const handler = (e) => {
      if (e.key === "Escape" && previewFilename) setPreviewFilename(null);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [fullscreen, previewFilename]);

  // Init inline map once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    mapInstance.current = L.map(mapRef.current, {
      zoomControl: true,
      scrollWheelZoom: true,
    }).setView([39.5, -8.0], 6);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 18,
    }).addTo(mapInstance.current);
  }, []);

  // Update inline markers when photos change
  useEffect(() => {
    if (!mapInstance.current) return;
    buildMarkers(mapInstance.current, photos, clusterRef, (info) => {
      if (onPhotoClick) {
        onPhotoClick(info);
      } else {
        setPreviewFilename(info.filename);
      }
    }, boundsRef);
  }, [photos, onPhotoClick]);

  const openFullscreen = useCallback(() => setFullscreen(true), []);
  const closeFullscreen = useCallback(() => setFullscreen(false), []);
  const recenter = useCallback(() => {
    if (mapInstance.current && boundsRef.current?.length) {
      mapInstance.current.fitBounds(boundsRef.current, { padding: [30, 30], maxZoom: 14 });
    }
  }, []);

  return (
    <div
      className={className || "w-full h-[250px] rounded-xl overflow-hidden border border-border"}
      style={{ position: "relative" }}
    >
      <div className="absolute top-2 right-2 z-[1000] flex gap-1.5">
        <button
          type="button"
          onClick={recenter}
          className="bg-background/90 backdrop-blur-sm border border-border rounded-md p-1.5 hover:bg-accent transition-colors"
          data-testid="photos-map-recenter"
          aria-label={t("map.controls.recenter", "Centrar na totalidade da rota")}
          title={t("map.controls.recenter", "Centrar na totalidade da rota")}
        >
          <Locate className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={openFullscreen}
          className="bg-background/90 backdrop-blur-sm border border-border rounded-md p-1.5 hover:bg-accent transition-colors"
          data-testid="photos-map-fullscreen"
          aria-label={t("map.controls.open_fullscreen", "Ampliar mapa")}
          title={t("map.controls.open_fullscreen", "Ampliar mapa")}
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      </div>
      <div
        ref={mapRef}
        style={{ width: "100%", height: "100%" }}
        data-testid="photos-map"
      />
      {previewFilename && (
        <PhotoPreviewOverlay
          photos={photos}
          currentFilename={previewFilename}
          onClose={() => setPreviewFilename(null)}
          onNavigate={setPreviewFilename}
        />
      )}
      {fullscreen && createPortal(
        <FullscreenMap photos={photos} onClose={closeFullscreen} onPhotoClick={onPhotoClick} />,
        document.body
      )}
    </div>
  );
}
